import { DateRange } from '@/components/Molecules/FaceliftDatePicker';
import createHttp from '@/config/http';
import type { ResponseMetaData } from '@/types/service';

export interface AppCrashReport {
	id: string;
	type?: string;
	moduleName?: string;
	userName?: string;
	timestamp?: string;
	action?: string;
	serviceName?: string;
	errorMessage?: string;
	userAgent?: string;
	environment?: string;
	component?: string;
	[key: string]: any; // Allow additional fields
}

const http = createHttp(import.meta.env.VITE_CRASH_URL);
/**
 * Fetch paginated frontend error reports from backend
 * Used by App Crash Report table component
 */
export const getFrontendErrorReports = async (
	page: number,
	limit: number,
	search: string,
	dateRange?: DateRange,
	type?: string,
): Promise<{ items: AppCrashReport[]; meta: ResponseMetaData }> => {
	const response = await http.get('/app-crash-log/all', {
		params: {
			start: dateRange?.startDate || '',
			end: dateRange?.endDate || '',
			page,
			limit,
			search,
			type,
		},
	});

	return {
		items: response.data.data || [],
		meta: {
			totalRecords: response.data.meta.total || 0,
			currentPage: page,
			itemsPerPage: limit,
			totalPages: response.data.meta.totalPages || 0,
		},
	};
};

export interface ScoreCardData {
	title: string;
	value: number | string;
}

export interface ChartDataItem {
	title: string;
	value: number;
}

export interface ChartCategory {
	category: string;
	data: ChartDataItem[];
}

/**
 * Fetch score cards data from backend
 * Used by App Crash Report overview component
 */
export const getScoreCards = async (): Promise<ScoreCardData[]> => {
	const response = await http.get('/app-crash-log/score-cards');
	return response.data.data || response.data || [];
};

/**
 * Fetch chart data from backend
 * Used by App Crash Report overview component
 */
export const getChartData = async (
	startDate?: string,
	endDate?: string,
): Promise<ChartCategory[]> => {
	const response = await http.get('/app-crash-log/chart-data', {
		params: {
			start: startDate || '',
			end: endDate || '',
		},
	});
	return response.data.data || response.data || [];
};

/**
 * Fetch crash report details by ID from backend
 * Used by App Crash Report details modal
 */
export const getCrashReportDetails = async (id: string): Promise<AppCrashReport> => {
	const response = await http.get(`/app-crash-log/details/${id}`);
	return response.data.data || response.data;
};

